import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import './dashboard.css';

const API_URL = import.meta.env.VITE_API_URL;

/* ===============================================================
   ヘルパー関数（コンポーネント外に配置）
   =============================================================== */

/** 数値を2桁にゼロ埋め */
const pad2 = (n) => String(n).padStart(2, '0');

/** 日本語の曜日配列 */
const JWD = ['日', '月', '火', '水', '木', '金', '土'];

/**
 * 日付文字列と時刻文字列からDateオブジェクトを生成
 */
const toDateFromParts = (dStr, tStr) => {
  if (!dStr || !tStr) return null;
  const t = tStr.length === 5 ? `${tStr}:00` : tStr; // 'HH:MM' → 'HH:MM:SS'
  const s = `${dStr}T${t}`;
  const d = new Date(s);
  return isNaN(d) ? null : d;
};

/**
 * 日付フォーマット関数群
 */
const fmtYMD = (d) =>
  `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
const fmtYMDw = (d) => `${fmtYMD(d)}(${JWD[d.getDay()]})`;
const fmtHM = (d) => `${pad2(d.getHours())}：${pad2(d.getMinutes())}`; // コロンは全角

/**
 * 文字列（YYYY-MM-DD）から YYYY/MM/DD(曜) 形式に変換
 */
const formatYMDwFromStr = (dStr) => {
  if (!dStr) return '—';
  const d = new Date(dStr);
  if (isNaN(d)) return dStr;
  return fmtYMDw(d);
};

/**
 * 開始・終了の日付時刻から期間文字列を生成
 * 同日: YYYY/MM/DD(曜)　HH：MM～HH：MM
 * 別日: YYYY/MM/DD(曜)～YYYY/MM/DD(曜)　HH：MM～HH：MM
 */
const formatPeriodFromParts = (sd, st, ed, et) => {
  const s = toDateFromParts(sd, st);
  const e = toDateFromParts(ed, et);

  if (s && e) {
    const sameDay =
      s.getFullYear() === e.getFullYear() &&
      s.getMonth() === e.getMonth() &&
      s.getDate() === e.getDate();
    if (sameDay) return `${fmtYMDw(s)}　${fmtHM(s)}～${fmtHM(e)}`;
    return `${fmtYMDw(s)}～${fmtYMDw(e)}　${fmtHM(s)}～${fmtHM(e)}`;
  }
  if (s) return `${fmtYMDw(s)}　${fmtHM(s)}`;
  if (e) return `${fmtYMDw(e)}　${fmtHM(e)}`;
  return '—';
};


const formatPeriodForEvent = (ev) => {
  const { start_date, end_date, start_time, end_time, times_unified } = ev;
  const isSingleDay = start_date === end_date;

  if (isSingleDay) {
    return formatPeriodFromParts(start_date, start_time, end_date, end_time);
  }
  if (times_unified) {
    const s = new Date(start_date);
    const e = new Date(end_date);
    const st = start_time ? toDateFromParts(start_date, start_time) : null;
    const et = end_time   ? toDateFromParts(end_date,   end_time)   : null;
    const timePart = (st && et) ? `　${fmtHM(st)}～${fmtHM(et)}` : '';
    return `${fmtYMDw(s)}～${fmtYMDw(e)}${timePart}`;
  }
  // 日程ごとに時刻が異なる場合
  const s = new Date(start_date);
  const e = new Date(end_date);
  return `${fmtYMDw(s)}～${fmtYMDw(e)}`;
};

// 全テーブル共通の列幅
const TableColGroup = () => (
  <colgroup>
    <col style={{ width: '22%' }} />  {/* 名称/日付/全体 */}
    <col style={{ width: '14%' }} />  {/* 参加予定数 */}
    <col style={{ width: '14%' }} />  {/* 参加人数 */}
    <col style={{ width: '16%' }} />  {/* 平均滞在 */}
    <col style={{ width: '17%' }} />  {/* 参加予定数(指定項目別) */}
    <col style={{ width: '17%' }} />  {/* 参加人数(指定項目別) */}
  </colgroup>
);

/**
 * 集計カード（スマホ表示用）
 * title: カード見出し（開催者名/日付など。全体テーブルでは省略）
 * fields: [{ label, value }] の配列
 */
const MetricsCard = ({ title, fields }) => (
  <div className="metricsCard">
    {title && <div className="metricsCard__title">{title}</div>}
    {fields.map((f, i) => (
      <div className="metricsCard__row" key={i}>
        <span className="metricsCard__label">{f.label}</span>
        <span className="metricsCard__value">{f.value}</span>
      </div>
    ))}
  </div>
);

/* ===============================================================
   サブコンポーネント
   =============================================================== */

/**
 * イベント行コンポーネント
 * イベント一覧の各行を表示
 */
const EventRow = ({ ev, active, onSelect }) => {
  // 期間テキストを生成
const periodTxt = formatPeriodForEvent(ev);

  return (
    <button
      type="button"
      className={`eventRow ${active ? 'is-active' : ''}`}
      onClick={() => onSelect(ev.event_id)}
      aria-pressed={active}
      title={ev.event_title}
    >
      {/* タイトル行：左にタイトル、右にステータスバッジ */}
      <div className="er-titleRow">
        <div className="er-title">{ev.event_title}</div>
        <div className="er-badges">
          <span className="badge">{ev.status || '-'}</span>
        </div>
      </div>
      {/* 期間表示 */}
      <div className="er-meta">{periodTxt}</div>
    </button>
  );
};

/* ===============================================================
   メインコンポーネント
   =============================================================== */

const AdminDashboard = () => {
  /* ---------------------------------------------------------------
     State定義
     --------------------------------------------------------------- */
  // イベント一覧関連
  const [events, setEvents] = useState([]);
  const [selectedId, setSelectedId] = useState('');

  // フィルター関連
  const [query, setQuery] = useState(''); // タイトル検索クエリ
  const [startFilter, setStartFilter] = useState(''); // 開始日フィルタ (YYYY-MM-DD)
  const [endFilter, setEndFilter] = useState(''); // 終了日フィルタ (YYYY-MM-DD)

  // メトリクス関連
  const [metrics, setMetrics] = useState(null);

  // UI状態関連
  const [err, setErr] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  // 項目別カウント
  const [selectionFilter, setSelectionFilter] = useState([]);
  const [selectionDropdownOpen, setSelectionDropdownOpen] = useState(false);

  /* ---------------------------------------------------------------
     API呼び出し関数
     --------------------------------------------------------------- */

  /**
   * イベント一覧を取得
   * 初回ロード時に呼び出される
   */
  const fetchEvents = async () => {
    setLoadingList(true);
    setErr('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/dashboard-list/`);

      if (res.data?.success) {
        setEvents(res.data.items);
        // 最初のイベントを自動選択
        if (res.data.items.length > 0) {
          setSelectedId(String(res.data.items[0].event_id));
        }
      } else {
        setErr('イベント一覧の取得に失敗しました');
      }
    } catch (e) {
      console.error(e);
      setErr('イベント一覧の取得に失敗しました');
    } finally {
      setLoadingList(false);
    }
  };

  /**
   * 選択されたイベントのメトリクスを取得
   */
  const fetchMetrics = async (id) => {
    if (!id) return;

    setLoadingMetrics(true);
    setErr('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/dashboard-metrics/`, {
        params: { event_id: id },
      });

      if (res.data?.success) {
        setMetrics(res.data);
      } else {
        setErr('メトリクスの取得に失敗しました');
      }
    } catch (e) {
      console.error(e);
      setErr(
        e.response?.status === 403 ? '権限がありません' : 'メトリクスの取得に失敗しました'
      );
    } finally {
      setLoadingMetrics(false);
    }
  };

  /* ---------------------------------------------------------------
     useMemo（計算結果のメモ化）
     --------------------------------------------------------------- */

  /**
   * 選択中のイベントオブジェクトを取得
   */
  const selectedEv = useMemo(
    () => events.find((ev) => String(ev.event_id) === String(selectedId)),
    [events, selectedId]
  );

  /**
   * 詳細表示用の期間文字列を生成
   * 一覧APIの分割フィールドを優先、無ければmetricsのISOをフォールバック
   */
  const detailPeriod = useMemo(() => {
    if (!metrics?.event) return '—';
    return formatPeriodForEvent(metrics.event);
  }, [metrics]);

  /**
   * フィルタリングされたイベント一覧
   * タイトル検索と期間フィルタを適用
   */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const startQ = startFilter ? new Date(`${startFilter}T00:00:00`) : null;
    const endQ = endFilter ? new Date(`${endFilter}T23:59:59`) : null;

    return events.filter((e) => {
      // タイトルでフィルタ
      const titleMatch =
        !q || (e.event_title || '').toLowerCase().includes(q);

      // 期間でフィルタ
      const es = toDateFromParts(e.start_date, e.start_time);
      const ee = toDateFromParts(e.end_date, e.end_time);

      // 期間フィルタが指定されていない場合はタイトルのみで判定
      if (!startQ && !endQ) return titleMatch;

      // 日時データが不完全な場合は除外
      if (!es || !ee) return false;

      // 入力範囲 [startQ, endQ] と イベント [es, ee] の重なり判定
      const startOk = !endQ || es <= endQ;
      const endOk = !startQ || ee >= startQ;
      const rangeMatch = startOk && endOk;

      return titleMatch && rangeMatch;
    });
  }, [events, query, startFilter, endFilter]);

  /* ---------------------------------------------------------------
     useEffect（副作用処理）
     --------------------------------------------------------------- */

  // 初回ロード時にイベント一覧を取得
  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 選択イベント変更時にメトリクスを取得
  useEffect(() => {
    if (selectedId) {
      fetchMetrics(selectedId);
      setSelectionFilter([]);           // ← 追加
      setSelectionDropdownOpen(false);  // ← 追加
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);


  const selectionDropdownRef = React.useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (selectionDropdownRef.current && !selectionDropdownRef.current.contains(e.target)) {
        setSelectionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  /* ---------------------------------------------------------------
     レンダリング関数
     --------------------------------------------------------------- */

  /**
   * エラーメッセージを表示
   */
  const renderError = () => err && <div className="error-alert">{err}</div>;

  /**
   * イベント一覧ヘッダー部分を表示
   */
  const renderEventListHeader = () => (
    <div className="eventListHeader">
      <div className="listTitle">イベント一覧</div>
      {loadingList ? (
        <span className="muted">読み込み中…</span>
      ) : (
        <span className="muted">{filtered.length}件</span>
      )}
      {/* タイトル検索入力 */}
      <input
        className="searchInput"
        placeholder="タイトルで検索"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {/* 開始日フィルタ */}
      <input
        type="date"
        className="dateInput"
        value={startFilter}
        onChange={(e) => setStartFilter(e.target.value)}
        title="開始日フィルタ"
      />
      {/* 終了日フィルタ */}
      <input
        type="date"
        className="dateInput"
        value={endFilter}
        onChange={(e) => setEndFilter(e.target.value)}
        title="終了日フィルタ"
      />
    </div>
  );

  /**
   * イベント一覧（左側パネル）を表示
   */
  const renderEventList = () => (
    <div className="eventListCard">
      {renderEventListHeader()}
      <div className="eventList">
        {filtered.length === 0 && !loadingList && (
          <div className="empty">対象のイベントがありません</div>
        )}
        {filtered.map((ev) => (
          <EventRow
            key={ev.event_id}
            ev={ev}
            active={String(ev.event_id) === String(selectedId)}
            onSelect={setSelectedId}
          />
        ))}
      </div>
    </div>
  );

  /**
   * イベント詳細ヘッダー部分を表示
   */
  const renderDetailHeader = () => (
    <div className="detailHeader">
      <div className="listTitle">イベント詳細</div>
      {loadingMetrics && <span className="muted">読み込み中…</span>}
    </div>
  );


  const renderSelectionDropdown = () => {
    const groups = metrics?.selection_groups || [];
    const items = groups.map(g => ({
      key:   String(g.event_group_id),
      label: g.group_name,
    }));

    const toggleItem = (key) => {
      setSelectionFilter(prev =>
        prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
      );
    };
    const allKeys = items.map(it => it.key);
    const allChecked = allKeys.length > 0 && allKeys.every(k => selectionFilter.includes(k));
    const toggleAll = () => {
      setSelectionFilter(allChecked ? [] : allKeys);
    };

    return (
      <div className="selectionBar">
        <span className="selectionBar__label">指定項目：</span>
        <div className="selectionDropdown" ref={selectionDropdownRef}>
          <button
            type="button"
            className="selectionDropdown__toggle"
            onClick={() => setSelectionDropdownOpen(o => !o)}
          >
            <span>
              {selectionFilter.length === 0
                ? '項目を選択'
                : `${selectionFilter.length}項目 選択中`}
            </span>
            <span>{selectionDropdownOpen ? '▲' : '▼'}</span>
          </button>
          {selectionDropdownOpen && (
            <div className="selectionDropdown__menu">
              {items.length === 0 ? (
                <div className="selectionDropdown__empty">項目がありません</div>
              ) : (
                <>
                  <label className="selectionDropdown__item selectionDropdown__item--all">
                    <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                    <span>すべて選択</span>
                  </label>
                  {items.map(it => (
                    <label key={it.key} className="selectionDropdown__item">
                      <input
                        type="checkbox"
                        checked={selectionFilter.includes(it.key)}
                        onChange={() => toggleItem(it.key)}
                      />
                      <span>{it.label}</span>
                    </label>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  /**
 * 項目別カウント計算（予定/参加の組み合わせユニーク数）
 * predicate: (participant) => boolean でフィルタする
 */
  const calcSelectionCount = (participantsData, predicate) => {
    if (selectionFilter.length === 0) {
      return { planned: null, actual: null };  // プルダウン未選択 → '-' で表示
    }
    const plannedKeys = new Set();
    const actualKeys  = new Set();
    participantsData.forEach(p => {
      if (p.status !== '参加') return;
      if (!predicate(p)) return;
      const key = selectionFilter
        .map(gid => {
          const v = p.answers[gid];
          return v == null ? '__NULL__' : v;
        })
        .join('||');
      plannedKeys.add(key);
      if (p.checked_in) actualKeys.add(key);
    });
    return { planned: plannedKeys.size, actual: actualKeys.size };
  };


  // 値を表示する（null は '-'）
  const fmtCell = (v) => (v == null || v === '' ? '-' : v);

  /**
   * 全体テーブル（カードの置き換え）
   */
  const renderOverallTable = () => {
    const partsData = metrics?.selection_participants || [];
    const sel = calcSelectionCount(partsData, () => true);

    return (
      <>
        <div className="dashTableWrap">
          <table className="dashTable">
            <TableColGroup />
            <thead>
              <tr>
                <th></th>
                <th>参加予定数</th>
                <th>参加人数</th>
                <th>平均滞在（分）</th>
                <th>参加予定数(指定項目別)</th>
                <th>参加人数(指定項目別)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><b>全体</b></td>
                <td>{metrics.overall.planned}</td>
                <td>{metrics.overall.actual}</td>
                <td>{metrics.overall.avg_stay_min}</td>
                <td>{fmtCell(sel.planned)}</td>
                <td>{fmtCell(sel.actual)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="metricsCardList">
          <MetricsCard
            fields={[
              { label: '参加予定数', value: metrics.overall.planned },
              { label: '参加人数', value: metrics.overall.actual },
              { label: '平均滞在（分）', value: metrics.overall.avg_stay_min },
              { label: '参加予定数(指定項目別)', value: fmtCell(sel.planned) },
              { label: '参加人数(指定項目別)', value: fmtCell(sel.actual) },
            ]}
          />
        </div>
      </>
    );
  };

  /**
   * 開催者別（トータル）
   */
  const renderHostMetricsTable = () => {
    const rows = metrics?.per_host ?? [];
    const partsData = metrics?.selection_participants || [];

    return (
      <>
        <h3 className="sectionH3">開催者別(トータル)</h3>
        <div className="dashTableWrap">
          <table className="dashTable">
            <TableColGroup />                              {/* ← 追加 */}
            <thead>
              <tr>
                <th>開催者</th>
                <th>参加予定数</th>
                <th>参加人数</th>
                <th>平均滞在（分）</th>
                <th>参加予定数(指定項目別)</th>
                <th>参加人数(指定項目別)</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty">データがありません</td>
                </tr>
              ) : (
                rows.map((r, i) => {
                  const sel = calcSelectionCount(partsData, p => p.host_id === r.host_id);
                  return (
                    <tr key={i}>
                      <td>{r.host_name}</td>
                      <td>{r.planned}</td>
                      <td>{r.actual}</td>
                      <td>{r.avg_stay_min}</td>
                      <td>{fmtCell(sel.planned)}</td>
                      <td>{fmtCell(sel.actual)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="metricsCardList">
          {rows.length === 0 ? (
            <div className="empty">データがありません</div>
          ) : (
            rows.map((r, i) => {
              const sel = calcSelectionCount(partsData, p => p.host_id === r.host_id);
              return (
                <MetricsCard
                  key={i}
                  title={r.host_name}
                  fields={[
                    { label: '参加予定数', value: r.planned },
                    { label: '参加人数', value: r.actual },
                    { label: '平均滞在（分）', value: r.avg_stay_min },
                    { label: '参加予定数(指定項目別)', value: fmtCell(sel.planned) },
                    { label: '参加人数(指定項目別)', value: fmtCell(sel.actual) },
                  ]}
                />
              );
            })
          )}
        </div>
      </>
    );
  };

  /**
   * 日別（全体）
   */
  const renderDailyMetricsTable = () => {
    const rows = metrics?.per_day ?? [];
    const eventInfo = metrics?.event;
    const partsData = metrics?.selection_participants || [];

    if (!eventInfo?.start_date || !eventInfo?.end_date) return null;
    if (eventInfo.start_date === eventInfo.end_date) return null;

    return (
      <>
        <h3 className="sectionH3">日別(トータル)</h3>
        <div className="dashTableWrap">
          <table className="dashTable">
            <TableColGroup />
            <thead>
              <tr>
                <th>日付</th>
                <th>参加予定数</th>
                <th>参加人数</th>
                <th>平均滞在（分）</th>
                <th>参加予定数(指定項目別)</th>
                <th>参加人数(指定項目別)</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty">データがありません</td>
                </tr>
              ) : (
                rows.map((d, i) => {
                  // 日別の「項目別カウント」は check_in_date でフィルタ
                  // ※「参加予定数」はこのテーブルでは持っていないので '-' 表示
                  const sel = calcSelectionCount(
                    partsData,
                    p => p.check_in_date === d.date
                  );
                  return (
                    <tr key={i}>
                      <td>{formatYMDwFromStr(d.date)}</td>
                      <td>-</td>
                      <td>{d.actual}</td>
                      <td>{d.avg_stay_min}</td>
                      <td>-</td>
                      <td>{fmtCell(sel.actual)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="metricsCardList">
          {rows.length === 0 ? (
            <div className="empty">データがありません</div>
          ) : (
            rows.map((d, i) => {
              const sel = calcSelectionCount(
                partsData,
                p => p.check_in_date === d.date
              );
              return (
                <MetricsCard
                  key={i}
                  title={formatYMDwFromStr(d.date)}
                  fields={[
                    { label: '参加予定数', value: '-' },
                    { label: '参加人数', value: d.actual },
                    { label: '平均滞在（分）', value: d.avg_stay_min },
                    { label: '参加予定数(指定項目別)', value: '-' },
                    { label: '参加人数(指定項目別)', value: fmtCell(sel.actual) },
                  ]}
                />
              );
            })
          )}
        </div>
      </>
    );
  };

  /**
   * 開催者別（日別）
   */
  const renderHostDailyMetricsTable = () => {
    const rows = metrics?.per_host_daily ?? [];
    const eventInfo = metrics?.event;
    const partsData = metrics?.selection_participants || [];

    if (!rows || rows.length === 0) return null;
    if (!eventInfo?.start_date || !eventInfo?.end_date) return null;
    if (eventInfo.start_date === eventInfo.end_date) return null;

    // 日付ごとにグルーピング
    const groupedByDate = rows.reduce((acc, r) => {
      const key = r.date;
      if (!acc[key]) acc[key] = [];
      acc[key].push(r);
      return acc;
    }, {});
    const sortedDates = Object.keys(groupedByDate).sort();

    return (
      <>
        {sortedDates.map((date) => {
          const list = groupedByDate[date];
          return (
            <div key={date} className="hostDailyBlock">
              <h3 className="sectionH3">{formatYMDwFromStr(date)}開催者別</h3>
              <div className="dashTableWrap">
                <table className="dashTable">
                  <TableColGroup />
                  <thead>
                    <tr>
                      <th>開催者</th>
                      <th>参加予定数</th>
                      <th>参加人数</th>
                      <th>平均滞在（分）</th>
                      <th>参加予定数(指定項目別)</th>
                      <th>参加人数(指定項目別)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((r, i) => {
                      const sel = calcSelectionCount(
                        partsData,
                        p => p.host_id === r.host_id && p.check_in_date === date
                      );
                      return (
                        <tr key={`${date}-${i}`}>
                          <td>{r.host_name}</td>
                          <td>-</td>
                          <td>{r.actual}</td>
                          <td>{r.avg_stay_min}</td>
                          <td>-</td>
                          <td>{fmtCell(sel.actual)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="metricsCardList">
                {list.map((r, i) => {
                  const sel = calcSelectionCount(
                    partsData,
                    p => p.host_id === r.host_id && p.check_in_date === date
                  );
                  return (
                    <MetricsCard
                      key={`${date}-${i}`}
                      title={r.host_name}
                      fields={[
                        { label: '参加予定数', value: '-' },
                        { label: '参加人数', value: r.actual },
                        { label: '平均滞在（分）', value: r.avg_stay_min },
                        { label: '参加予定数(指定項目別)', value: '-' },
                        { label: '参加人数(指定項目別)', value: fmtCell(sel.actual) },
                      ]}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </>
    );
  };


  /**
   * イベント詳細を表示
   */
  const renderDetailCard = () => (
    <div className="detailCard">
      {renderDetailHeader()}

      {!metrics ? (
        <div className="empty">イベントを選択してください</div>
      ) : (
        <>
          {/* イベント基本情報 */}
          <div className="eventHeader">
           
           <div>
              {!metrics.event.times_unified && metrics.event.schedules?.length > 1 ? (
                <>
                  {metrics.event.schedules.map((s, i) => (
                    <div key={i} style={{ display: 'flex' }}>
                      <span style={{ minWidth: '3.5em', fontWeight: i === 0 ? 'bold' : 'normal', color: i === 0 ? 'inherit' : 'transparent' }}>
                        期間
                      </span>
                      <span>：{formatYMDwFromStr(s.date)}　{s.start_time?.slice(0, 5)}～{s.end_time?.slice(0, 5)}</span>
                    </div>
                  ))}
                </>
              ) : (
                <div><b>期間</b>：{detailPeriod}</div>
              )}
            </div>
          </div>
      
          {/* 項目別カウント用プルダウン（全テーブルに効く） */}
          {renderSelectionDropdown()}

          {/* 全体テーブル（カードの置き換え） */}
          {renderOverallTable()}

          {/* 開催者別メトリクス（トータル） */}
          {renderHostMetricsTable()}

          {/* 日別メトリクス（全体）※複数日のとき */}
          {renderDailyMetricsTable()}

          {/* 開催者別メトリクス（日別）※複数日のとき */}
          {renderHostDailyMetricsTable()}
        </>
      )}
    </div>
  );

  /* ---------------------------------------------------------------
     メインレンダリング
     --------------------------------------------------------------- */
  return (
    <div className="adminDash">
      {/* エラー表示 */}
      {renderError()}

      {/* イベント一覧 */}
      {renderEventList()}

      {/* イベント詳細 */}
      {renderDetailCard()}
    </div>
  );
};

export default AdminDashboard;
